import { useEffect, useState } from "react";
import { Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { COMPLETION_REASON_MAX_LENGTH } from "@shyne/shared/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { billingExceptionText } from "@/lib/billing-exception";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import type { AppointmentJoin } from "@/repositories/appointments.repository";
import { completeAppointment } from "@/services/appointment-completion.service";

interface CompleteAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentJoin;
  refusal?: string | null;
  onSuccess?: (updatedAppointment: AppointmentJoin) => void;
}

export default function CompleteAppointmentModal({ isOpen, onClose, appointment, refusal, onSuccess }: CompleteAppointmentModalProps) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) setReason("");
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setLoading(true);
    try {
      const completed = await completeAppointment(appointment.id, reason);
      toast.success("Appointment marked as complete");
      onSuccess?.(completed);
      onClose();
    } catch (error) {
      console.error("Failed to complete appointment:", error);
      toast.error(fieldActionMessage(error, "Failed to complete appointment"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Mark as Complete
          </DialogTitle>
          <DialogDescription>
            {refusal ?? `${appointment.billing_exception ? `${billingExceptionText(appointment.billing_exception)}. ` : ""}Upload what is missing, or give the reason it is complete without it.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="completion-reason">Reason *</Label>
          <Textarea
            id="completion-reason"
            placeholder="Why this appointment is complete without what is missing..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={COMPLETION_REASON_MAX_LENGTH}
            disabled={loading}
            className="resize-none"
          />
          <p className="text-sm text-muted-foreground">The reason is recorded in the appointment's history.</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!reason.trim() || loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Completing...
              </>
            ) : (
              "Mark as Complete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
